import { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Menu, MenuItem, Tooltip } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { getCatalogSuggestion } from '../../api/catalog';
import { useSnackbar } from '../snackbar';

ItemAISuggestion.propTypes = {
	description: PropTypes.string,
	aiEnabled: PropTypes.bool,
	onSuggestionSelected: PropTypes.func.isRequired,
};

export default function ItemAISuggestion({ description, aiEnabled = true, onSuggestionSelected }) {
	const [aiLoading, setAiLoading] = useState(false);
	const [aiMenuAnchor, setAiMenuAnchor] = useState(null);
	const [aiSuggestions, setAISuggestions] = useState([]);
	const { enqueueSnackbar } = useSnackbar();

	const handleAiSuggest = async (e) => {
		const anchorEl = e.currentTarget;

		if (!aiEnabled) {
			enqueueSnackbar('AI is currently disabled.', { variant: 'warning' });
			return;
		}

		try {
			setAiLoading(true);
			const { data } = await getCatalogSuggestion(description);

			if (data.success && data.suggestions?.length) {
				setAISuggestions(data.suggestions);
				setAiMenuAnchor(anchorEl);
			} else {
				enqueueSnackbar(data.errorMessage || 'AI suggestion failed', { variant: 'error' });
			}
		} catch (err) {
			enqueueSnackbar('AI suggestion failed', { variant: 'error' });
		} finally {
			setAiLoading(false);
		}
	};

	const clearAISuggestions = () => {
		setAiMenuAnchor(null);
		setAISuggestions([]);
	};

	return (
		<>
			<Tooltip title="Suggest with AI">
				<span>
					<LoadingButton
						onClick={handleAiSuggest}
						disabled={!aiEnabled}
						loading={aiLoading}
						sx={{
							borderRadius: 16,
							border: '1px solid',
							borderColor: 'divider',
							backgroundColor: 'primary.main',
							color: 'primary.contrastText',
							minWidth: 0,
							minHeight: 32,
							px: 1,
							gap: 0.75,
							textTransform: 'none',
							whiteSpace: 'nowrap',
							'&:hover': {
								backgroundColor: 'primary.dark',
							},
							opacity: aiEnabled ? 1 : 0.5,
						}}
					>
						<img
							src="/assets/bobAI/bobAIlogo_2.png"
							alt="bobAI Icon"
							style={{ width: 16, height: 16 }}
						/>
						<Box component="span" sx={{ fontSize: 11, lineHeight: 1.2 }}>
							BobAI can help rewrite the description
						</Box>
					</LoadingButton>
				</span>
			</Tooltip>

			{aiSuggestions?.length > 0 && aiMenuAnchor && (
				<Menu
					id="ai-item-description-suggestions"
					anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
					anchorEl={aiMenuAnchor}
					open={!!aiMenuAnchor}
					onClose={clearAISuggestions}
				>
					{aiSuggestions.map((descriptionSuggestion, index) => (
						<MenuItem
							key={index}
							onClick={() => {
								onSuggestionSelected(descriptionSuggestion);
								clearAISuggestions();
							}}
						>
							{descriptionSuggestion}
						</MenuItem>
					))}
				</Menu>
			)}
		</>
	);
}
